import { PropsWithChildren, useState } from 'react';
import StyledButton from '../styled/StyledButton';
import CircuitDetailsImageGallery from './CircuitDetailsImageGallery';
import clsx from 'clsx';
import { useTranslation } from '@/i18n';

interface CircuitDetailsImagesCardProps {
  circuitId: string;
  images: string[];
  accessToken?: string;
}

interface ImageContainerWithButtonProps {
  imgSrc: string;
  hasShowButton?: boolean;
  fullHeight?: boolean;
  bigWidth?: boolean;
  onShowGallery: () => void;
  showAllPhotosLabel: string;
}

const ImageContainerWithButton = ({
  imgSrc,
  hasShowButton = false,
  fullHeight = false,
  bigWidth = false,
  onShowGallery,
  showAllPhotosLabel,
}: ImageContainerWithButtonProps) => (
  <ImageContainer
    imageSrc={imgSrc}
    onClick={onShowGallery}
    fullHeight={fullHeight}
    bigWidth={bigWidth}
  >
    {hasShowButton && (
      <StyledButton
        variant="link"
        title={showAllPhotosLabel}
        className="absolute px-3 py-2 bg-white rounded-lg text-sm font-normal text-black z-10 bottom-4 right-4"
        onClick={onShowGallery}
      />
    )}
  </ImageContainer>
);

interface ImageGalleryWrapperProps {
  circuitId: string;
  images: string[];
  isOpen: boolean;
  onClose: () => void;
  accessToken?: string;
}

const ImageGalleryWrapper = ({
  circuitId,
  images,
  isOpen,
  onClose,
  accessToken,
}: ImageGalleryWrapperProps) => {
  if (!isOpen) return null;
  return (
    <CircuitDetailsImageGallery
      circuitId={circuitId}
      images={images}
      onClose={onClose}
      accessToken={accessToken}
    />
  );
};

const CircuitDetailsImagesCard = ({
  circuitId,
  images,
  accessToken,
}: CircuitDetailsImagesCardProps) => {
  const { t } = useTranslation();
  const [isSlideShowOpen, setIsSlideShowOpen] = useState(false);

  const showAllPhotosLabel = t('circuits.editorial.details.seeAllPhotos');

  if (!images.length) return null;
  return images.length > 2 ? (
    <div className="flex flex-col md:flex-row w-full md:max-h-[500px] [@media_(min-width:1921px)]:max-h-[700px] [@media_(min-width:2400px)]:max-h-[800px] gap-4">
      <ImageContainerWithButton
        imgSrc={images[0]}
        bigWidth
        onShowGallery={() => setIsSlideShowOpen(true)}
        showAllPhotosLabel={showAllPhotosLabel}
      />
      <div className="flex flex-col gap-4 md:w-1/3">
        <ImageContainerWithButton
          imgSrc={images[1]}
          fullHeight
          onShowGallery={() => setIsSlideShowOpen(true)}
          showAllPhotosLabel={showAllPhotosLabel}
        />
        <ImageContainerWithButton
          imgSrc={images[2]}
          fullHeight
          hasShowButton
          onShowGallery={() => setIsSlideShowOpen(true)}
          showAllPhotosLabel={showAllPhotosLabel}
        />
      </div>
      <ImageGalleryWrapper
        circuitId={circuitId}
        images={images}
        isOpen={isSlideShowOpen}
        onClose={() => setIsSlideShowOpen(false)}
        accessToken={accessToken}
      />
    </div>
  ) : (
    <div className="w-full md:max-h-[500px] [@media_(min-width:1921px)]:max-h-[700px] [@media_(min-width:2400px)]:max-h-[800px]">
      <ImageContainerWithButton
        imgSrc={images[0]}
        fullHeight
        hasShowButton
        onShowGallery={() => setIsSlideShowOpen(true)}
        showAllPhotosLabel={showAllPhotosLabel}
      />
      <ImageGalleryWrapper
        circuitId={circuitId}
        images={images}
        isOpen={isSlideShowOpen}
        onClose={() => setIsSlideShowOpen(false)}
        accessToken={accessToken}
      />
    </div>
  );
};

export default CircuitDetailsImagesCard;

const ImageContainer = ({
  imageSrc,
  fullHeight = false,
  bigWidth = false,
  onClick,
  children,
}: PropsWithChildren<{
  imageSrc: string;
  onClick?: () => void;
  fullHeight?: boolean;
  bigWidth?: boolean;
}>) => (
  <div
    className={clsx(
      'relative rounded-lg overflow-hidden hover:cursor-pointer',
      {
        'w-full md:w-2/3': bigWidth,
        'h-full': fullHeight,
      },
    )}
    onClick={onClick}
  >
    <img src={imageSrc} className="w-full h-full object-cover object-center" />
    {children}
  </div>
);
